import {
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/20/solid";
import { FaDiscord } from "react-icons/fa";
import React, { useState } from "react";
import { useRouteError, isRouteErrorResponse, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { logError } from "./lib/api/Api";
import SEO from "./components/SEO";
import { Button, Card } from "./components/ui";

export default function ErrorPage() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  // Log the error to the console for debugging
  console.error(error);

  // Log error to backend
  React.useEffect(() => {
    if (error) {
      const errorMessage = isRouteErrorResponse(error)
        ? error.statusText || String(error.status)
        : error instanceof Error
        ? error.message
        : typeof error === "string"
        ? error
        : "Unknown Error";

      const errorStack = error instanceof Error ? error.stack : undefined;
      const originalError = error instanceof Error ? error : undefined;

      logError({
        message: errorMessage,
        stack: errorStack,
        info: isRouteErrorResponse(error)
          ? {
              status: error.status,
              statusText: error.statusText,
              data: error.data,
            }
          : error instanceof Error
          ? { name: error.name }
          : error,
        originalError,
      });
    }
  }, [error]);

  // Helper function to extract the error message safely
  const getErrorMessage = () => {
    if (isRouteErrorResponse(error)) {
      return error.statusText || String(error.status);
    } else if (error instanceof Error) {
      return error.message;
    } else if (typeof error === "string") {
      return error;
    } else {
      return "Unknown Error";
    }
  };

  const getErrorStack = () => {
    if (error instanceof Error && error.stack) return error.stack;
    return null;
  };

  const handleGoHome = () => {
    navigate("/");
  };

  const handleReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex flex-1 items-center justify-center p-4 min-h-[60vh]">
      <SEO title="Error" description="An unexpected error has occurred." noindex />
      <Card
        padding="lg"
        className="w-full max-w-2xl shadow-2xl space-y-6 text-center"
      >
        {/* Warning Icon */}
        <div className="flex justify-center">
          <div className="bg-red-900/20 p-4 rounded-full">
            <ExclamationTriangleIcon className="h-16 w-16 text-red-500" />
          </div>
        </div>

        {/* Title and Description */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-white">
            {t("ErrorPage.title", "Oops! Something went wrong.")}
          </h1>
          <p className="text-gray-300">
            {t(
              "ErrorPage.friendlyMessage",
              "Something unexpected happened on our end. Our team has been notified."
            )}
          </p>
        </div>

        {/* Expandable Technical Details */}
        <div className="text-left">
          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="cursor-pointer inline-flex items-center gap-1 text-sm text-gray-400 hover:text-gray-200 transition-colors"
            aria-expanded={showDetails}
          >
            {showDetails ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronRightIcon className="h-4 w-4" />
            )}
            {t("ErrorPage.technicalDetails", "Technical details")}
          </button>
          {showDetails && (
            <div className="mt-3 bg-gray-900/50 rounded-xl p-4 border border-gray-700/50 max-h-64 overflow-auto">
              <pre className="text-red-400 text-xs font-mono whitespace-pre-wrap break-words">
                {getErrorMessage()}
                {getErrorStack() && `\n\n${getErrorStack()}`}
              </pre>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
          <Button onClick={handleGoHome}>
            {t("ErrorPage.goHomeButton", "Go Back Home")}
          </Button>

          <Button variant="secondary" onClick={handleReload}>
            {t("ErrorPage.reloadButton", "Reload Page")}
          </Button>

          <Button
            asChild
            iconStart={<FaDiscord className="w-5 h-5" />}
            className="bg-discord hover:bg-discord-dark from-discord to-discord hover:from-discord-dark hover:to-discord-dark"
          >
            <a
              href={import.meta.env.VITE_DISCORD_INVITE}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("ErrorPage.reportOnDiscord", "Report on Discord")}
            </a>
          </Button>
        </div>
      </Card>
    </div>
  );
}
